<template>
  <AppCard
    class="ProjectsGridTemplate-card pa-5 text-text-secondary rounded-lg"
    data-locator="room-card"
    :ripple="false"
    :data-name="project.name"
    :data-type="project.type"
    @click="accessRoom"
  >
    <v-row no-gutters class="ProjectsGrid-header mb-4">
      <v-col class="d-flex align-center justify-space-between">
        <span v-if="project.nbParticipants">
          <AppTooltip>
            <template #activator="{ props }">
              <div class="d-flex align-center column-gap-1" v-bind="props">
                <app-material-icon
                  name="group"
                />
                <span>{{ project.nbParticipants }}</span>
              </div>
            </template>
            <span v-t="{
                path: 'projects.ProjectsGrid.nbParticipants',
                plural: project.nbParticipants,
                args: { nbParticipants: project.nbParticipants },
              }"
            />
          </AppTooltip>
        </span>

        <!-- Menu -->
        <div class="ml-auto">
          <ProjectsFoldersGridMenu v-if="projectsFoldersTree.length > 0 && !isRecentRoomsView" :item="project" isRoom ></ProjectsFoldersGridMenu>
        </div>
      </v-col>
    </v-row>
    <v-row no-gutters class="mt-2">
      <v-col cols="12">
        <template v-if="project.name === null && isPending">
          <v-skeleton-loader
            type="text"
            :height="PROJECT_CARD_NAME_ROW_HEIGHT"
            class="ProjectsGrid-skeleton-loader"
          />
        </template>

        <div v-else class="d-flex align-center">
          <RoomTypeIcon class="mr-2" :room="project" data-locator="room-type-icon" />
          <AppTooltip>
            <template #activator="{ props }">
              <h3 v-bind="props" class="text-truncated flex-grow-1 text-text-primary" data-locator="room-name">{{ project.name }}</h3>
            </template>
            {{ project.name }}
          </AppTooltip>
          <AppTooltip v-if="project.daysUntilClosure !== null">
            <template #activator="{ props }">
              <app-material-icon
                name="warning"
                filled
                color="icon-warning"
                class="ml-1"
                v-bind="props"
              />
            </template>
            <span v-t="{
              path: 'projects.ProjectsGrid.nbDaysBeforeClosure',
              plural: project.daysUntilClosure,
              args: { nbDays: project.daysUntilClosure },
            }" />
          </AppTooltip>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="project.label" class="mb-3">
      <v-col cols="12" class="py-0">
        <template v-if="isPending">
          <v-skeleton-loader
            type="text"
            :height="PROJECT_CARD_INFORMATION_ROW_HEIGHT"
            class="ProjectsGrid-skeleton-loader"
          />
        </template>
        <AppProjectLabel
          v-else
          :label="project.label"
          class="flex-shrink-0"
          data-locator="room-label"
        />
      </v-col>
    </v-row>
    <v-row v-if="project.cmRef" class="mt-2">
      <v-col class="py-0">
        <AppTooltip>
          <template v-slot:activator="{ props }">
            <div class="d-flex align-center" v-bind="props">
              <app-material-icon name="tag" class="mr-1" />
              <span class="text-truncated" data-locator="room-ref">{{ project.cmRef }}</span>
            </div>
          </template>
          <span>{{ project.cmRef }}</span>
        </AppTooltip>
      </v-col>
    </v-row>
    <v-row>
      <template v-if="project.createdAt === null && isPending">
        <v-col cols="12">
          <v-skeleton-loader
            type="text"
            :height="PROJECT_CARD_INFORMATION_ROW_HEIGHT"
            class="ProjectsGrid-skeleton-loader"
          />
        </v-col>
      </template>

      <template v-else>
        <v-col v-if="project.createdAt" class="py-0">
          <AppTooltip>
            <template #activator="{props}">
            <span v-bind="props" class="d-inline-flex align-center">
              <app-material-icon
                name="schedule"
                class="mr-2"
              />
              <span data-locator="room-creation-date">{{displayDate(project.createdAt)}}</span>
            </span>
            </template>
            <span>{{$t('projects.ProjectsGrid.creationDate')}}</span>
          </AppTooltip>
        </v-col>
        <v-col v-if="project.expectedClosingDate" class="py-0">
          <AppTooltip>
            <template #activator="{props}">
              <div class="d-flex align-center" v-bind="props">
                <app-material-icon
                  name="calendar_today"
                  class="mr-2"
                />
                <span  data-locator="room-due-date">{{displayDate(project.expectedClosingDate)}}</span>
              </div>
            </template>
            <span>{{$t('projects.ProjectsGrid.dueDate')}}</span>
          </AppTooltip>
        </v-col>
      </template>
    </v-row>
    <v-row>
      <v-col>
        <div class="my-1">
          <template v-if="documentsCount === null || isPending">
            <v-skeleton-loader
              type="text"
              :height="PROJECT_CARD_INFORMATION_ROW_HEIGHT"
              class="ProjectsGrid-skeleton-loader"
            />
          </template>
          <template v-else>
            <router-link
              class="ProjectsGrid-link"
              :to="{ name: ROOM_DOCUMENTS_ROUTE_NAME, params: { mnemo: project.mnemo, roomType: computedRoomPathType } }"
              data-locator="documents-page-link"
              @click.stop
            >
              <span class="count-value">{{ documentsCount }}</span>
              <span>{{ $t('projects.ProjectsGrid.documents') }}</span>
            </router-link>
          </template>
        </div>
        <div v-if="project.isDataroom" class="my-1">
          <template v-if="questionsCount === null || isPending">
            <v-skeleton-loader
              type="text"
              :height="PROJECT_CARD_INFORMATION_ROW_HEIGHT"
              class="ProjectsGrid-skeleton-loader"
            />
          </template>
          <template v-else>
            <router-link
              class="ProjectsGrid-link"
              :to="{ name: QUESTION_ANSWER_LIST_ROUTE_NAME, params: { mnemo: project.mnemo, roomType: computedRoomPathType } }"
              data-locator="q-and-a-page-link"
              @click.stop
            >
              <span class="count-value">
                {{ questionsCount }}
              </span>
              <span>
                {{ $t('projects.ProjectsGrid.questions') }}
              </span>
            </router-link>
          </template>
        </div>
        <template v-if="!project.isDataroom">
          <div class="my-1">
            <template v-if="tasksCount === null || isPending">
              <v-skeleton-loader
                type="text"
                :height="PROJECT_CARD_INFORMATION_ROW_HEIGHT"
                class="ProjectsGrid-skeleton-loader"
              />
            </template>
            <template v-else>
              <router-link
                class="ProjectsGrid-link"
                @click.stop
                :to="{ name: ROOM_CHECKLIST_ROUTE_NAME, params: { mnemo: project.mnemo, roomType: computedRoomPathType } }"
                data-locator="checklist-page-link"
              >
                <span class="count-value">{{ tasksCount }}</span>
                <span>{{ $t('projects.ProjectsGrid.tasks') }}</span>
              </router-link>
            </template>
          </div>
          <div class="text-truncated my-1">
            <template v-if="documentsToSignCount === null || isPending">
              <v-skeleton-loader type="text" :height="PROJECT_CARD_INFORMATION_ROW_HEIGHT" class="ProjectsGrid-skeleton-loader" />
            </template>
            <template v-else>
              <AppTooltip>
                <template #activator="{ props }">
                  <router-link
                    class="ProjectsGrid-link"
                    @click.stop
                    :to="{ name: SIGNING_CHECKLIST_ROUTE_NAME, params: { mnemo: project.mnemo, roomType: computedRoomPathType } }"
                    data-locator="signing-checklist-page-link"
                    v-bind="props"
                  >
                    <span class="count-value">{{ documentsToSignCount }} </span>
                    <span v-if="project.isPM">{{ $t('projects.ProjectsGrid.pendingSignaturesPM') }}</span>
                    <span v-else>{{ $t('projects.ProjectsGrid.pendingSignatures') }}</span>
                    </router-link>
                  </template>
                  <span>{{ documentsToSignCount }} &nbsp; </span>
                  <span v-if="project.isPM">{{ $t('projects.ProjectsGrid.pendingSignaturesPM') }}</span>
                  <span v-else>{{ $t('projects.ProjectsGrid.pendingSignatures') }}</span>
                </AppTooltip>
            </template>
          </div>

          <div class="my-1">
            <template v-if="originalsCount === null || isPending">
              <v-skeleton-loader
                type="text"
                :height="PROJECT_CARD_INFORMATION_ROW_HEIGHT"
                class="ProjectsGrid-skeleton-loader"
              />
            </template>
            <template v-else>
              <router-link
                class="ProjectsGrid-link"
                :to="{ name: ROOM_SIGNED_DOCUMENTS_ROUTE_NAME, params: { mnemo: project.mnemo, roomType: computedRoomPathType } }"
                data-locator="signed-documents-page-link"
                @click.stop
              >
                <span class="count-value">{{ originalsCount }}</span>
                <span>{{ $t('projects.ProjectsGrid.originals') }}</span>
              </router-link>
            </template>
          </div>
        </template>
      </v-col>
    </v-row>
  </AppCard>
</template>

<script>
import { mapMutations, mapState } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppProjectLabel from '@/common/AppProjectLabel.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import RoomTypeIcon from '@/common/RoomTypeIcon.vue'
import { ISOToShortenedDate } from '@/common/utils/dates'
import { getRoomRouteType } from '@/common/utils/rooms'
import ProjectsFoldersGridMenu from '@/projects/ProjectsFoldersGridMenu.vue'
import {
  QUESTION_ANSWER_LIST_ROUTE_NAME,
  ROOM_CHECKLIST_ROUTE_NAME,
  ROOM_DOCUMENTS_ROUTE_NAME,
  ROOM_ROUTE_NAME,
  ROOM_SIGNED_DOCUMENTS_ROUTE_NAME,
  SIGNING_CHECKLIST_ROUTE_NAME,
} from '@/router/routerData'
import { SET_ROOM_UNDER_NDA_DIALOG } from '@/store/modules/room/mutation_types'
// Current width, rounded to the nearest 10, of the content width for projectGrid cards
// Used especially to get a truncate that truncate pretty much at the same point on tables
export const PROJECT_CARD_CONTENT_WIDTH = 280
// Total height of the project card, taking into account internal paddings _and_
// a 2px difference for low hanging letters
export const PROJECT_CARD_TOTAL_HEIGHT = 304

// Height of a row in the project card information section
// Used to make sure the skeleton loaders match the height of the real content
export const PROJECT_CARD_INFORMATION_ROW_HEIGHT = 21
export const PROJECT_CARD_NAME_ROW_HEIGHT = 20.4

export default {
  name: 'ProjectsGrid',
  components: {
    AppCard,
    AppProjectLabel,
    AppTooltip,
    ProjectsFoldersGridMenu,
    RoomTypeIcon,
  },
  props: {
    project: {
      type: Object,
      required: true,
    },
    isPending: {
      type: Boolean,
      default: false,
    },
    isRecentRoomsView: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      QUESTION_ANSWER_LIST_ROUTE_NAME,
      ROOM_CHECKLIST_ROUTE_NAME,
      ROOM_DOCUMENTS_ROUTE_NAME,
      ROOM_ROUTE_NAME,
      ROOM_SIGNED_DOCUMENTS_ROUTE_NAME,
      SIGNING_CHECKLIST_ROUTE_NAME,
      PROJECT_CARD_INFORMATION_ROW_HEIGHT,
      PROJECT_CARD_NAME_ROW_HEIGHT,
      PROJECT_CARD_TOTAL_HEIGHT_STYLE: PROJECT_CARD_TOTAL_HEIGHT + 'px',
    }
  },
  computed: {
    ...mapState('projectsFolders', ['projectsFoldersTree']),
    documentsCount () {
      if (this.project.countsForRoom) {
        if (this.project.countsForRoom.files) {
          return this.project.countsForRoom.files
        } else if (this.project.countsForUser.files) {
          return this.project.countsForUser.files
        } else return 0
      } else if (this.project.countsForUser) {
        return this.project.countsForUser.files ? this.project.countsForUser.files : 0
      }
      return null
    },
    questionsCount () {
      if (this.project.countsForRoom) {
        if (this.project.countsForRoom.questions) {
          return this.project.countsForRoom.questions
        } else if (this.project.countsForUser.questions) {
          return this.project.countsForUser.questions
        } else return 0
      } else if (this.project.countsForUser) {
        return this.project.countsForUser.questions ? this.project.countsForUser.questions : 0
      }
      return null
    },
    originalsCount () {
      if (this.project.countsForUser) {
        if (this.project.countsForUser.originals) {
          return this.project.countsForUser.originals
        } else return 0
      }
      return null
    },
    tasksCount () {
      if (this.project.countsForRoom) {
        if (this.project.countsForRoom.tasksTagged) {
          return this.project.countsForRoom.tasksTagged
        } else if (this.project.countsForUser.tasksTagged) {
          return this.project.countsForUser.tasksTagged
        } else return 0
      } else if (this.project.countsForUser) {
        return this.project.countsForUser.tasksTagged ? this.project.countsForUser.tasksTagged : 0
      }
      return null
    },
    documentsToSignCount () {
      if (this.project.countsForRoom) {
        if (this.project.countsForRoom.signaturesPending) {
          return this.project.countsForRoom.signaturesPending
        } else if (this.project.countsForUser.signaturesPending) {
          return this.project.countsForUser.signaturesPending
        } else return 0
      } else if (this.project.countsForUser) {
        return this.project.countsForUser.signaturesPending ? this.project.countsForUser.signaturesPending : 0
      }
      return null
    },
    computedRoomPathType () {
      return getRoomRouteType(this.project)
    },
  },
  methods: {
    ...mapMutations('room', [SET_ROOM_UNDER_NDA_DIALOG]),
    displayDate (date) {
      return ISOToShortenedDate(date)
    },
    accessRoom () {
      if (!this.project.needToSignNda) {
        this.$router.push({ name: ROOM_ROUTE_NAME, params: { mnemo: this.project.mnemo, roomType: this.computedRoomPathType } })
      } else {
        this.SET_ROOM_UNDER_NDA_DIALOG({ mnemo: this.project.mnemo, isOpen: true })
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.ProjectsGridTemplate-card {
  border: get-border('border-secondary');
  &:hover {
    border: get-border('border-secondary-hover');
  }

  // This is set to the natural maximal height of the card based on a legal project
  // With 4 informations rows, a label, a reference row and a date row
  height: v-bind('PROJECT_CARD_TOTAL_HEIGHT_STYLE');
}
.ProjectsGrid-link {
  text-decoration: none;
  color: rgb(var(--v-theme-text-link));

  &:hover, &:focus {
    text-decoration: underline;
    color: rgb(var(--v-theme-text-link-hover));
    .count-value {
      text-decoration: underline;
    }
  }
  .count-value {
    display: inline-block;
    width: 35px;
    margin-right: 8px;
    text-align: right;
  }
}

.ProjectsGrid-skeleton-loader {

  :deep(.v-skeleton-loader__text) {
    // This keeps the skeleton loader height consistent with the real content
    margin: 0;
  }
}
.ProjectsGrid-header {
  // We set the height of this row manually to reserve space
  // and avoid uneven margins when we have a label in it,
  // which increases its natural height
  height: 26px;
}
</style>
